import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { TopologyData, BaselineData, toShortHostname } from '../TopologyData';
import { computeIsisSpt, extractP2pPath } from '../utils/pathfinder';
import TopologyGraph from './TopologyGraph';

interface Props {
    data: TopologyData;
    baseline: BaselineData | null;
    source: string;
    target: string;
    onClose: () => void;
}

const DEFAULT_W = 720;
const DEFAULT_H = 520;
const MIN_W = 480;
const MIN_H = 340;

const P2pPanel: React.FC<Props> = ({ data, baseline, source, target, onClose }) => {
    const [pos, setPos] = useState({ x: window.innerWidth - DEFAULT_W - 24, y: 100 });
    const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
    const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
    const resizeRef = useRef<{ startX: number; startY: number; origW: number; origH: number } | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    // --- Drag logic ---
    const onDragStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };

        const onMove = (ev: MouseEvent) => {
            if (!dragRef.current) return;
            const dx = ev.clientX - dragRef.current.startX;
            const dy = ev.clientY - dragRef.current.startY;
            setPos({
                x: Math.max(0, Math.min(window.innerWidth - 200, dragRef.current.origX + dx)),
                y: Math.max(0, Math.min(window.innerHeight - 60, dragRef.current.origY + dy)),
            });
        };
        const onUp = () => {
            dragRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [pos]);

    // --- Resize logic ---
    const onResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: size.w, origH: size.h };

        const onMove = (ev: MouseEvent) => {
            if (!resizeRef.current) return;
            const dx = ev.clientX - resizeRef.current.startX;
            const dy = ev.clientY - resizeRef.current.startY;
            setSize({
                w: Math.max(MIN_W, resizeRef.current.origW + dx),
                h: Math.max(MIN_H, resizeRef.current.origH + dy),
            });
        };
        const onUp = () => {
            resizeRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [size]);

    // --- Path computation ---
    const resolve = useCallback((term: string) => {
        const lower = term.toLowerCase();
        return data.nodes.find(
            (n) =>
                n.id.toLowerCase() === lower ||
                n.hostname.toLowerCase() === lower ||
                toShortHostname(n.hostname) === lower
        );
    }, [data]);

    const srcNode = resolve(source);
    const tgtNode = resolve(target);

    const { sptResult, p2pEdgeIds, p2pNodeIds, srcId, tgtId } = useMemo(() => {
        if (!srcNode || !tgtNode || srcNode.id === tgtNode.id) {
            return { sptResult: null, p2pEdgeIds: null, p2pNodeIds: null, srcId: null, tgtId: null };
        }
        const spt = computeIsisSpt(data.links, srcNode.id);
        const p2p = extractP2pPath(spt, tgtNode.id);
        return {
            sptResult: spt,
            p2pEdgeIds: p2p?.edgeIds ?? null,
            p2pNodeIds: p2p?.nodeIds ?? null,
            srcId: srcNode.id,
            tgtId: tgtNode.id,
        };
    }, [data, srcNode, tgtNode]);

    const hopCount = p2pEdgeIds ? p2pEdgeIds.size : 0;

    return (
        <div
            ref={panelRef}
            role="dialog"
            aria-label={`Point-to-point path: ${source} to ${target}`}
            className="fixed z-[9000] flex flex-col rounded-2xl border shadow-2xl overflow-hidden select-none"
            style={{
                left: pos.x,
                top: pos.y,
                width: size.w,
                height: size.h,
                background: 'var(--color-semantic-bg-primary)',
                borderColor: 'var(--color-semantic-border-secondary)',
                boxShadow: '0 8px 40px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.04)',
                transition: 'none',
            }}
        >
            {/* Draggable header */}
            <div
                className="flex items-center gap-2.5 px-4 py-2.5 border-b shrink-0"
                style={{
                    borderColor: 'var(--color-semantic-border-secondary)',
                    cursor: 'grab',
                    userSelect: 'none',
                }}
                onMouseDown={onDragStart}
            >
                {/* Drag grip */}
                <svg width="10" height="14" viewBox="0 0 10 14" className="text-semantic-text-secondary/40 shrink-0">
                    <circle cx="2" cy="2" r="1.2" fill="currentColor" />
                    <circle cx="8" cy="2" r="1.2" fill="currentColor" />
                    <circle cx="2" cy="7" r="1.2" fill="currentColor" />
                    <circle cx="8" cy="7" r="1.2" fill="currentColor" />
                    <circle cx="2" cy="12" r="1.2" fill="currentColor" />
                    <circle cx="8" cy="12" r="1.2" fill="currentColor" />
                </svg>
                <span
                    className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded shrink-0"
                    style={{ background: '#ff4089', color: 'white' }}
                >
                    P2P
                </span>
                <span className="text-xs font-semibold text-semantic-text-primary font-mono truncate">
                    {source}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="#ff4089" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    className="shrink-0">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                </svg>
                <span className="text-xs font-semibold text-semantic-text-primary font-mono truncate">
                    {target}
                </span>
                {hopCount > 0 && (
                    <span className="text-[10px] text-semantic-text-secondary shrink-0">
                        {hopCount} hop{hopCount !== 1 ? 's' : ''}
                    </span>
                )}
                <button
                    onClick={onClose}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="ml-auto p-1 rounded-lg hover:bg-semantic-bg-secondary
                        transition-colors cursor-pointer border-none bg-transparent shrink-0"
                    title="Close (Esc)"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        className="text-semantic-text-secondary">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            {/* Graph area */}
            <div className="flex-1 min-h-0">
                {!srcNode || !tgtNode ? (
                    <div className="flex items-center justify-center h-full text-sm text-semantic-text-secondary">
                        Could not resolve nodes: {source}, {target}
                    </div>
                ) : (
                    <TopologyGraph
                        data={data}
                        baseline={baseline}
                        onNodeClick={Function.prototype as () => void}
                        onEdgeClick={Function.prototype as () => void}
                        sptResult={sptResult}
                        sptMode
                        p2pSource={srcId}
                        p2pTarget={tgtId}
                        p2pEdgeIds={p2pEdgeIds}
                        p2pNodeIds={p2pNodeIds}
                    />
                )}
            </div>

            {/* Resize handle (bottom-right corner) */}
            <div
                className="absolute bottom-0 right-0 w-4 h-4"
                style={{ cursor: 'nwse-resize' }}
                onMouseDown={onResizeStart}
            >
                <svg width="16" height="16" viewBox="0 0 16 16" className="text-semantic-text-secondary/30">
                    <line x1="14" y1="4" x2="4" y2="14" stroke="currentColor" strokeWidth="1.5" />
                    <line x1="14" y1="8" x2="8" y2="14" stroke="currentColor" strokeWidth="1.5" />
                    <line x1="14" y1="12" x2="12" y2="14" stroke="currentColor" strokeWidth="1.5" />
                </svg>
            </div>
        </div>
    );
};

export default P2pPanel;
